/**
 * O catálogo de colunas do Faturômetro, com as fórmulas exatas da tela antiga.
 *
 * Cada `value()` aqui foi transcrito de KpiTable.vue, célula por célula. Onde a
 * fórmula antiga tem defeito, ele foi COPIADO e marcado — a regra nº 1 é que
 * nenhum número mude, e "consertar de passagem" é a forma mais provável de a
 * tela nova discordar da antiga sem ninguém entender por quê. Os defeitos estão
 * listados em DEFEITOS_HERDADOS e presos por teste.
 *
 * CUIDADO COM OS RÓTULOS
 *
 * Três rótulos dizem o inverso da conta: "EA/EF" divide EF por EA, "EF/EP"
 * divide EP por EF e "EA/EP" divide EP por EA. São taxas de conversão, e
 * conversão se lê "de cada EA, quantas viram EF". Renomear muda a leitura de
 * todo mundo que já usa a tela; a conta é que manda, e é a que está aqui.
 *
 * MACROCOMPONENTES
 *
 * São 33 colunas. O desenho aprovado mostra 13 por padrão e guarda o resto
 * atrás do painel de Componentes, agrupadas por assunto — é o que `group`
 * serve. Um líder que acompanha Momento X liga o grupo inteiro de uma vez.
 */

const toNumber = value => {
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}

/** Divisão que não explode: sem denominador, a tela antiga mostra 0. */
const ratio = (numerator, denominator) => (denominator ? numerator / denominator : 0)

/**
 * Os macrocomponentes e as cores deles.
 *
 * As tríades base/tint/label não foram inventadas para a tela nova: elas vêm da
 * tela ANTIGA (as sete faixas coloridas de KpiTable.vue) e o design as
 * reaproveitou. `base` pinta o filete sob a faixa e o marcador do painel de
 * Componentes; `tint` é o fundo da faixa; `label` é o texto sobre o tint, e foi
 * escolhido para passar de 4.5:1 sobre ele — por isso não é o `base`, que é
 * claro demais para texto.
 */
/*
 * `inkDark` e a identidade do grupo no tema escuro, e existe porque no escuro
 * a faixa NAO tem fundo tingido.
 *
 * O protocolo aprovado so desenhou o tema claro -- `grep -i dark` nos nove
 * artboards nao acha nada. No claro o grupo se identifica por um bloco pastel
 * com texto escuro; repetir isso no escuro nao funciona, porque pastel a alfa
 * baixa sobre #343d55 vira mancha acinzentada e os cinco blocos deixam de se
 * distinguir entre si. Entao no escuro o fundo fica uniforme e quem carrega a
 * identidade e o TEXTO e o filete de 2px -- que e o que a unica regra de dark
 * que ja funcionava neste componente faz.
 *
 * Para isso o `base` nao serve: #3a4dc1 (Momento X) sobre #343d55 e quase o
 * mesmo tom, e a faixa some. `inkDark` e o mesmo matiz clareado ate passar de
 * 4.5:1 sobre o fundo do cabecalho. Dois deles (#9aa5ea e #4ddb92) ja sao os
 * valores que style.scss usa para os cargos no escuro -- reaproveitados de
 * proposito, para a tela nao ter dois azuis-claros diferentes.
 */
const GROUPS = [
  {
    key: 'entrevista', label: 'Entrevista', base: '#00cfe8', tint: '#e3fafc', ink: '#007482', inkDark: '#3ddced',
  },
  {
    key: 'recomendacoes', label: 'Recomendações', base: '#ea5455', tint: '#fceaea', ink: '#a3302f', inkDark: '#f08182',
  },
  {
    key: 'eficiencia', label: 'Eficiência', base: '#98ce00', tint: '#f4fae3', ink: '#5c7c00', inkDark: '#b6e63a',
  },
  {
    key: 'momento_x', label: 'Momento X', base: '#3a4dc1', tint: '#e9ebf8', ink: '#3a4dc1', inkDark: '#9aa5ea',
  },
  {
    key: 'parceria', label: 'Parcerias', base: '#f26237', tint: '#feeee9', ink: '#ca390d', inkDark: '#f28f6b',
  },
  {
    key: 'assinaturas', label: 'Assinaturas', base: '#82868b', tint: '#f3f2f7', ink: '#5e5873', inkDark: '#b4b7bd',
  },
  {
    key: 'producao', label: 'Resumos de faturamento', base: '#28c76f', tint: '#e7f9ef', ink: '#197b44', inkDark: '#4ddb92',
  },
]

/**
 * Aproximações conscientes desta tela. Cada uma tem teste que falha se alguém
 * "consertar" sem decidir.
 *
 * Aqui moravam também os dois defeitos copiados da tela antiga — a guarda
 * errada de MXF/MXC e o denominador bruto do R$/EP. Ficaram presos enquanto
 * paridade com ela era o critério; com o KPI antigo saindo, foram consertados
 * em vez de herdados.
 */
const DEFEITOS_HERDADOS = [
  {
    coluna: 'customers_quantity',
    o_que: 'é contagem DISTINTA e mesmo assim entra em SUM_FIELDS, somada pela árvore',
    efeito: 'cliente que comprou de dois consultores conta duas vezes no subtotal — medido: 0 casos em junho/2026, 40 de 1.357 no ano (2,9%). Como a tela lê uma competência por vez, na prática é exato',
  },
]

/**
 * As colunas que a leitura de especialista NAO atribui.
 *
 * Na tela elas aparecem como `—`, e nao como `0`. A diferenca entre "nao
 * produziu" e "nao e medido nesta leitura" foi exatamente o defeito que esta
 * perspectiva tinha antes, quando 27 colunas mostravam zero.
 *
 * SAO DOIS MOTIVOS DIFERENTES, e vale nao confundi-los:
 *
 * IMPOSSIVEL — Leads - Quente e PEF. O conceito de especialista nao existe
 * nessas origens: nem a tabela de leads nem a de `interviews` (que produz o
 * PEF) tem a coluna, e o BI tambem nao mede nenhuma das duas.
 *
 * DECIDIDO — REC PN, REC CL e o REC/EF que deriva delas. Aqui o especialista
 * TEM atribuicao possivel, e o BI ja a publica: `dna_consultant_metrics_monthly`
 * credita a REC ao especialista da entrevista feita do cliente que indicou, via
 * `lead.recommended_by`. O Faturometro nao segue, por decisao de Produto
 * (2026-09-17): a REC nao e relevante para a visao de especialista.
 *
 * A medicao que sustentou a decisao, em abril/2026: de 3.418 RECs, 1.290 tem o
 * token da indicacao (37,7%) e so 331 chegam a creditar um especialista —
 * 9,7%. Em junho sao 6,3%, e no ano 6,5%. Um numero 93% incompleto ao lado de
 * um EP completo le como comparavel e nao e. Alem disso a replica nem tem o
 * campo: `kpi.leads` guarda `customer_reference`, que e OUTRO campo (so 51% dos
 * 20.661 pares coincidem com `recommended_by` da origem), entao seguir o BI
 * custaria migration, backfill e mudanca no consumidor Kafka.
 *
 * Ou seja: aqui o Oraculo diverge do BI DE PROPOSITO, e isso esta registrado.
 * Quem for reabrir, reabra pela decisao — nao pela impossibilidade.
 */
const SEM_ATRIBUICAO_DE_ESPECIALISTA = [
  // Impossivel: a origem nao tem o conceito.
  'hot_leads_quantity',
  'pre_interviews_quantity',
  // Decidido: possivel no BI, fora de escopo nesta visao.
  'consultant_leads_quantity',
  'client_leads_quantity',
  // Deriva das duas de REC sobre EF: sem numerador, a razao nao significa nada.
  'rec_ef',
]

const semAtribuicaoDeEspecialista = key => SEM_ATRIBUICAO_DE_ESPECIALISTA.includes(key)

const COLUMNS = [
  // ---- Funil ------------------------------------------------------------
  {
    key: 'hot_leads_quantity',
    label: 'Leads - Quente',
    group: 'entrevista',
    type: 'int',
    value: t => toNumber(t.hot_leads_quantity),
  },
  {
    key: 'scheduled_interview_quantity',
    label: 'EA',
    hint: 'Entrevista agendada',
    group: 'entrevista',
    type: 'int',
    value: t => toNumber(t.scheduled_interview_quantity),
  },
  {
    key: 'pre_interviews_quantity',
    label: 'PEF',
    hint: 'Pré-entrevista feita',
    group: 'entrevista',
    type: 'int',
    value: t => toNumber(t.pre_interviews_quantity),
  },
  {
    key: 'done_interview_quantity',
    label: 'EF',
    hint: 'Entrevista feita',
    group: 'entrevista',
    type: 'int',
    value: t => toNumber(t.done_interview_quantity),
  },
  {
    key: 'interview_contract_quantity',
    label: 'EP',
    hint: 'Entrevista paga',
    group: 'entrevista',
    type: 'int',
    // A coluna desconta as que ainda estão em negociação. As razões abaixo NÃO
    // descontam — ver DEFEITOS_HERDADOS.
    value: t => toNumber(t.interview_contract_quantity) - toNumber(t.negotiation_interview_contract_quantity),
    detailBucket: 'ep',
  },
  {
    key: 'scheduled_negotiation_quantity',
    label: 'NEA',
    hint: 'Negociação agendada',
    group: 'entrevista',
    type: 'int',
    value: t => toNumber(t.scheduled_negotiation_quantity),
  },
  {
    key: 'negotiation_positive_feedbacks_quantity',
    label: 'NEP',
    hint: 'EPs em negociação paga',
    group: 'entrevista',
    type: 'int',
    // A tela antiga mostra negotiation_interview_contract_quantity nesta
    // coluna, e não o campo de mesmo nome. Copiado como está.
    value: t => toNumber(t.negotiation_interview_contract_quantity),
  },
  {
    key: 'consultant_leads_quantity',
    label: 'REC PN',
    hint: 'Recomendações de PN',
    group: 'recomendacoes',
    type: 'int',
    value: t => toNumber(t.consultant_leads_quantity),
  },
  {
    key: 'client_leads_quantity',
    label: 'REC CL',
    hint: 'Recomendações de cliente',
    group: 'recomendacoes',
    type: 'int',
    value: t => toNumber(t.client_leads_quantity),
  },

  // ---- Eficiência -------------------------------------------------------
  {
    key: 'rec_ef',
    label: 'REC/EF',
    hint: 'Recomendações por entrevista feita',
    group: 'recomendacoes',
    type: 'decimal',
    value: t => ratio(
      toNumber(t.consultant_leads_quantity) + toNumber(t.client_leads_quantity),
      toNumber(t.done_interview_quantity),
    ),
  },
  {
    key: 'ea_ef',
    label: 'EA/EF',
    hint: 'De cada entrevista agendada, quantas viram feita',
    group: 'eficiencia',
    type: 'percent',
    value: t => ratio(toNumber(t.done_interview_quantity), toNumber(t.scheduled_interview_quantity)) * 100,
  },
  {
    key: 'ef_ep',
    label: 'EF/EP',
    hint: 'De cada entrevista feita, quantas viram paga',
    group: 'eficiencia',
    type: 'percent',
    value: t => ratio(toNumber(t.interview_contract_quantity), toNumber(t.done_interview_quantity)) * 100,
  },
  {
    key: 'ea_ep',
    label: 'EA/EP',
    hint: 'De cada entrevista agendada, quantas viram paga',
    group: 'eficiencia',
    type: 'percent',
    value: t => ratio(toNumber(t.interview_contract_quantity), toNumber(t.scheduled_interview_quantity)) * 100,
  },
  {
    key: 'client_interview_contract_average',
    label: 'R$/EP',
    hint: 'Valor médio da entrevista paga',
    group: 'eficiencia',
    type: 'money',
    /**
     * Divide pelo EP EXIBIDO, o mesmo da coluna EP ao lado.
     *
     * Dividia pelo EP bruto, sem descontar as entrevistas em negociação, e a
     * coluna EP desconta. Conferir uma contra a outra na mesma linha não
     * fechava — e era isso que a tela antiga fazia. Com ela saindo, o defeito
     * perde o motivo de existir.
     *
     * Sem EP a razão não existe e vira `—`, pela mesma regra do resto.
     */
    value: t => {
      const eps = toNumber(t.interview_contract_quantity)
        - toNumber(t.negotiation_interview_contract_quantity)
      return eps ? toNumber(t.interview_contract_amount) / eps : NaN
    },
  },

  // ---- Momento X --------------------------------------------------------
  {
    key: 'scheduled_x_moment_quantity',
    label: 'MXA',
    hint: 'Momento X agendado',
    group: 'momento_x',
    type: 'int',
    value: t => toNumber(t.scheduled_x_moment_quantity),
  },
  {
    key: 'done_x_moment_quantity',
    label: 'MXF',
    hint: 'Momento X feito',
    group: 'momento_x',
    type: 'int',
    value: t => toNumber(t.done_x_moment_quantity),
  },
  {
    key: 'x_moment_positive_feedbacks_quantity',
    label: 'MXC',
    hint: 'Momento X com contrato',
    group: 'momento_x',
    type: 'int',
    value: t => toNumber(t.x_moment_positive_feedbacks_quantity),
  },
  {
    key: 'mxa_mxf',
    label: 'MXA/MXF',
    group: 'momento_x',
    type: 'percent',
    value: t => ratio(toNumber(t.done_x_moment_quantity), toNumber(t.scheduled_x_moment_quantity)) * 100,
  },
  {
    key: 'mxf_mxc',
    label: 'MXF/MXC',
    group: 'momento_x',
    type: 'percent',
    /**
     * A guarda pergunta pelo PRÓPRIO denominador.
     *
     * Ela perguntava por MXA e a divisão usava MXF: com Momento X agendado e
     * nenhum feito, a conta era `n / 0` e a célula mostrava `Infinity`. Veio
     * copiado da tela antiga e ficou preso enquanto paridade com ela era o
     * critério — deixou de ser, com o KPI antigo saindo.
     *
     * Sem MXF a razão não existe, e `format()` transforma o não-finito em `—`,
     * que é a mesma convenção de "não é medido" do resto da tela. `—` é a
     * resposta honesta; `Infinity` nunca foi resposta.
     */
    value: t => (toNumber(t.done_x_moment_quantity)
      ? (toNumber(t.x_moment_positive_feedbacks_quantity) / toNumber(t.done_x_moment_quantity)) * 100
      : NaN),
  },

  // ---- Parceria ---------------------------------------------------------
  {
    key: 'scheduled_partnership_quantity',
    label: 'PA',
    hint: 'Parceria agendada',
    group: 'parceria',
    type: 'int',
    value: t => toNumber(t.scheduled_partnership_quantity),
  },
  {
    key: 'done_partnership_quantity',
    label: 'PF',
    hint: 'Parceria feita',
    group: 'parceria',
    type: 'int',
    value: t => toNumber(t.done_partnership_quantity),
  },
  {
    key: 'partnership_positive_feedbacks_quantity',
    label: 'PC',
    hint: 'Parceria com contrato',
    group: 'parceria',
    type: 'int',
    value: t => toNumber(t.partnership_positive_feedbacks_quantity),
  },
  {
    key: 'pa_pf',
    label: 'PA/PF',
    group: 'parceria',
    type: 'percent',
    value: t => ratio(toNumber(t.done_partnership_quantity), toNumber(t.scheduled_partnership_quantity)) * 100,
  },
  {
    key: 'pf_pc',
    label: 'PF/PC',
    group: 'parceria',
    type: 'percent',
    value: t => ratio(
      toNumber(t.partnership_positive_feedbacks_quantity),
      toNumber(t.done_partnership_quantity),
    ) * 100,
  },

  // ---- Produção ---------------------------------------------------------
  {
    key: 'support_contract_quantity',
    label: 'Suporte',
    group: 'assinaturas',
    type: 'int',
    value: t => toNumber(t.support_contract_quantity),
    detailBucket: 'support',
  },
  {
    key: 'signature_contract_quantity',
    label: 'Acompanhamentos',
    group: 'assinaturas',
    type: 'int',
    value: t => toNumber(t.signature_contract_quantity),
    detailBucket: 'signature',
  },
  {
    key: 'renewal_contract_quantity',
    label: 'Renovações',
    group: 'assinaturas',
    type: 'int',
    value: t => toNumber(t.renewal_contract_quantity),
    detailBucket: 'renewal',
  },
  {
    key: 'interview_contract_amount',
    label: 'R$ de EP',
    group: 'producao',
    type: 'money',
    value: t => toNumber(t.interview_contract_amount),
    detailBucket: 'ep',
  },
  {
    key: 'subscription_contract_amount',
    label: 'R$ Assinatura',
    group: 'producao',
    type: 'money',
    // O dinheiro de assinatura vem de pagamento conciliado, não de contrato —
    // a tela antiga já lê subscription_revenue_reconciled aqui, apesar do nome
    // da coluna. Ver armadilha 3 do plano.
    value: t => toNumber(t.subscription_revenue_reconciled),
    detailBucket: 'subscription',
  },
  {
    key: 'renewal_contract_amount',
    label: 'R$ Renovações',
    group: 'producao',
    type: 'money',
    value: t => toNumber(t.renewal_contract_amount),
    detailBucket: 'renewal',
  },
  {
    key: 'other_financial_solutions_contract_amount',
    label: 'R$ Soluções',
    group: 'producao',
    type: 'money',
    value: t => toNumber(t.other_financial_solutions_contract_amount),
    detailBucket: 'other_solution',
  },
  {
    key: 'total_contract_amount',
    label: 'R$ Total',
    group: 'producao',
    type: 'money',
    value: t => toNumber(t.total_contract_amount),
    // A gaveta do total é a UNIÃO de EP + assinatura + outras soluções +
    // renovações, que são exatamente as quatro parcelas que compõem esta
    // coluna. `signature` e `support` ficam de fora porque são recortes de
    // assinatura: têm coluna própria mas não somam no total, e incluí-los
    // listaria o mesmo contrato duas vezes. Medido: as flags de solução não são
    // disjuntas (6.803 de 28.271 casam com duas ou mais), mas entre estas
    // quatro a sobreposição é zero. Ver KpiContractDetailService::TOTAL_BUCKET.
    detailBucket: 'total',
  },
  {
    key: 'ltv_cl',
    label: 'LTV/CL',
    hint: 'Faturamento total ÷ clientes distintos com contrato reportado',
    group: 'producao',
    type: 'money',
    /**
     * LTV por CLIENTE — a regra do card 1217381846456640.
     *
     * Dividia por `interview_contract_quantity`, a contagem de EP: cliente com
     * duas EPs contava duas vezes, e cliente que comprou só assinatura não
     * contava, embora a receita dele já estivesse no numerador. Não era LTV por
     * cliente nem por contrato — era receita total por EP, um híbrido sem nome,
     * e o `hint` antigo confessava: "não existe LTV por cliente".
     *
     * Agora existe. `customers_quantity` é o denominador, e o CL do rótulo
     * finalmente quer dizer o que diz.
     *
     * SEM O CAMPO, a coluna mostra `—` e não um número pela regra velha. É o
     * caso do backend anterior ao card: `format()` já transforma valor não
     * finito em `—`, que é a mesma convenção de "não é medido nesta leitura"
     * usada na perspectiva de especialista. Zero clientes cai no mesmo lugar, e
     * está certo: não se divide receita por nenhum cliente.
     *
     * NÃO confundir com `client_interview_contract_average` (R$/EP), que é
     * receita de EP ÷ contratos de EP. Essa é por CONTRATO, o nome está certo,
     * e ela continua como está.
     */
    value: t => {
      const clientes = toNumber(t.customers_quantity)
      return clientes ? toNumber(t.total_contract_amount) / clientes : NaN
    },
  },
]

/**
 * As 13 que aparecem sem ninguém pedir.
 *
 * Critério: o funil até EP, a conversão entre as etapas e o dinheiro. É o que
 * um líder olha todo dia; o resto entra pelo painel de Componentes.
 */
const DEFAULT_COLUMN_KEYS = [
  'hot_leads_quantity',
  'scheduled_interview_quantity',
  'pre_interviews_quantity',
  'done_interview_quantity',
  'interview_contract_quantity',
  'negotiation_positive_feedbacks_quantity',
  'ea_ef',
  'ef_ep',
  'interview_contract_amount',
  'subscription_contract_amount',
  'other_financial_solutions_contract_amount',
  'total_contract_amount',
  'ltv_cl',
]

/**
 * A coluna que não se tira.
 *
 * §24: "preservação de dados mínimos para identificação da linha e leitura do
 * total". A identificação é a árvore, que nunca sai; o total é esta.
 *
 * Sem isso havia um defeito calado: desmarcando tudo, o painel mostrava zero
 * colunas selecionadas e a tela continuava com as 13 do padrão, porque a
 * seleção vazia caía no padrão. Com uma coluna obrigatória a seleção nunca
 * fica vazia, e as duas passam a contar a mesma história.
 */
const REQUIRED_COLUMN_KEYS = ['total_contract_amount']

const isRequiredColumn = key => REQUIRED_COLUMN_KEYS.includes(key)

const columnByKey = key => COLUMNS.find(column => column.key === key)

/** Resolve uma seleção de chaves para colunas, na ordem do catálogo. */
const resolveColumns = keys => {
  const wanted = new Set(keys && keys.length ? keys : DEFAULT_COLUMN_KEYS)
  REQUIRED_COLUMN_KEYS.forEach(key => wanted.add(key))
  return COLUMNS.filter(column => wanted.has(column.key))
}

const columnsByGroup = () => GROUPS.map(group => ({
  ...group,
  columns: COLUMNS.filter(column => column.group === group.key),
}))

module.exports = {
  COLUMNS,
  SEM_ATRIBUICAO_DE_ESPECIALISTA,
  semAtribuicaoDeEspecialista,
  DEFAULT_COLUMN_KEYS,
  REQUIRED_COLUMN_KEYS,
  isRequiredColumn,
  DEFEITOS_HERDADOS,
  GROUPS,
  columnByKey,
  columnsByGroup,
  resolveColumns,
}
